import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { RoleMasterService, PrivilegeManagerService } from "../../services";
import { useSelector, useDispatch } from 'react-redux';
import Autocomplete from '@material-ui/lab/Autocomplete';
import {
    Backdrop,Card, CardContent, CircularProgress,
    Container, FormControlLabel, Grid, Switch,
    TextField, Typography} from '@material-ui/core';
import SuccessDialog from '../../components/shared/SuccessDialog';
import MaterialTable, { MTableToolbar, MTableHeader } from 'material-table'
import TableIcons from '../../materialTableIcons';
import '../GridStyleOverrides/materialTableHome.css'
import { SET_PRIV_MANAGER_ROLE_SELECTED_PERMISSIONS } from '../../actions/types';
import allowAction from '../../helpers/allowAction';

const useStyles = makeStyles((theme) => ({
    pageTitleDiv: {
        marginTop: 20,
    },
    pageTitle: {
        paddingTop: 6,
        paddingBottom: 6,
        paddingLeft: 0,
        fontSize: 18,
        color: theme.palette.text.secondary,
        textTransform: 'uppercase'
    },
    cardFiltersContainer: {
        marginTop: 20,
        marginBottom: 20,
        backgroundColor: theme.palette.background.paper,
        width: '100%'
    },
    cardContentForFiltersContainer: {
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'nowrap',
        backgroundColor: theme.palette.background.paper,
    },
    filterDropdownsContainer: {
        width: '100%',
    },
    gridItem: {
        display: 'flex',
        flexWrap: 'nowrap',
        flexDirection: 'row',
    },
    showAllAndFilterButtonsContainer: {
        display: 'flex',
        flexDirection: 'column',
        flexWrap: 'wrap',
        justifyContent: 'center',
        alignItems: 'center',
        marginLeft: 20,
    },
    showAllButton: {
        width: 140,
        marginBottom: 8,
    },
    filterButton: {
        width: 140,
        marginTop: 8,
    },
    tableContainer: {
        maxWidth: theme.breakpoints.keys[3]
    },
    table: {
        maxWidth: 1500,
    },
    permissionsTable: {
        maxWidth: 500
    },
    backdrop: {
        zIndex: theme.zIndex.drawer + 1,
    },
    enableButton: {
        width: 20,
        height: 20,
        borderStyle: 'none',
        borderColor: 'transparent',
        borderRadius: '50%',
        paddingTop: 0,
        paddingBottom: 0,
        paddingLeft: 0,
        paddingRight: 0,
        margin: 3,
        backgroundColor: '#7fbf5a',
        color: 'white'
    },
    disableButton: {
        width: 20,
        height: 20,
        borderStyle: 'none',
        borderColor: 'transparent',
        borderRadius: '50%',
        paddingTop: 0,
        paddingBottom: 0,
        paddingLeft: 0,
        paddingRight: 0,
        margin: 3,
        backgroundColor: '#d6625c',
        color: 'white'
    },
    formControlLabel: {
        marginLeft: 6
    }
}));

export default function PrivilegeManager() {
    const classes = useStyles();
    const dispatch = useDispatch();

    const theme = useTheme()
    const headerStyle = {
        backgroundColor: '#86C1D4',
        height: 49,
        textTransform: 'uppercase',
        color: 'white',
        paddingTop: 0,
        paddingBottom: 0
    }
    const linkRowStyle = {
        paddingTop: 0,
        paddingBottom: 0,
        height: 40
    }
    const middleRowStyle = {
        backgroundColor: theme.palette.type === 'light' ? theme.palette.grey[100] : theme.palette.grey[700],
        paddingTop: 0,
        paddingBottom: 0,
        height: 40
    }
    const childRowStyle = {
        backgroundColor: theme.palette.type === 'light' ? theme.palette.grey[200] : theme.palette.grey[600],
        paddingTop: 0,
        paddingBottom: 0,
        height: 32
    }

    const selectedRolePrivs = useSelector((state) => state.privilegeManager.privManagerRoleSelectedPermissions);

    const [isLoadingRoles, setIsLoadingRoles] = useState(true)
    const [isLoadingPages, setIsLoadingPages] = useState(true)
    const [isTableLoading, setIsTableLoading] = useState(true)
    const [successDialog, setSuccessDialog] = useState({ isOpen: false, title: '', subTitle: '' });

    const [roleOptions, setRoleOptions] = useState([])
    const [selectedRole, setSelectedRole] = useState({ rolId: 0, rolName: 'Default', rolDescription: 'Default' })

    const [pageNumber, setPageNumber] = useState(0)
    const [pageSize, setPageSize] = useState(5)

    const [pages, setPages] = useState([])
    const [rowParents, setRowParents] = useState([])
    const [rowChildren, setRowChildren] = useState([])

    const [permissionsArr, setPermissionsArr] = useState(selectedRolePrivs)

    useEffect(() => {
        async function fetchRoles() {
            let data = await RoleMasterService.getRoles();
            data.sort((a, b) => a.rolId - b.rolId);
            setRoleOptions(data);
            setIsLoadingRoles(false)
        }
        fetchRoles()
    }, []);

    useEffect(() => {
        async function fetchPages() {

            let data = await PrivilegeManagerService.getPages();
            setPages(data);

            let parents = data.filter(d => d.pagParentId === "0");
            setRowParents(parents)

            let children = data.filter(d => d.pagParentId !== "0");
            setRowChildren(children)

            setIsLoadingPages(false)
        }
        fetchPages()
    }, []);

    useEffect(() => {
        async function getPrivsByRole(roleObject) {
            let data = await PrivilegeManagerService.getPrivByRoleId(roleObject);
            setPermissionsArr(data)
            dispatch({
                type: SET_PRIV_MANAGER_ROLE_SELECTED_PERMISSIONS,
                payload: data,
            });
            setIsTableLoading(false)
        }
        getPrivsByRole(selectedRole);
    }, [selectedRole, dispatch])

    const handleRoleChange = (target, roleObj) => {
        setIsTableLoading(true)
        if (target === null) {
            roleObj = { rolId: 0, rolName: 'Default', rolDescription: 'Default' }
        }
        setSelectedRole(roleObj)
    };

    const handleClickPermission = (page, index) => {
        let permissionToChange = permissionsArr.find(o => o.rolPriPageId === page)
        if (permissionToChange.rolPriPrivileges === "1" && permissionToChange.rolPriPrivileges.length === 1) {
            permissionToChange.rolPriPrivileges = "0"
            for (let i = 0; i < permissionsArr.length; i++) {
                if (permissionsArr[i].rolPriPageId[0] === page) {
                    permissionsArr[i].rolPriPrivileges = "0".repeat(permissionsArr[i].rolPriPrivileges.length);
                }
            }
        } else if (permissionToChange.rolPriPrivileges === "0" && permissionToChange.rolPriPrivileges.length === 1) {
            permissionToChange.rolPriPrivileges = "1"
            for (let i = 0; i < permissionsArr.length; i++) {
                if (permissionsArr[i].rolPriPageId[0] === page) {
                    permissionsArr[i].rolPriPrivileges = "1".repeat(permissionsArr[i].rolPriPrivileges.length);
                }
            }
        } else if (permissionToChange.rolPriPrivileges[index] === "1") {
            permissionToChange.rolPriPrivileges = permissionToChange.rolPriPrivileges.substring(0, index) + '0' + permissionToChange.rolPriPrivileges.substring(index + 1);
        }
        //else if (index === 8 && permissionToChange.rolPriPrivileges[index] === "1") {
        //    permissionToChange.rolPriPrivileges = "0".repeat(permissionToChange.rolPriPrivileges.length)
        //} else if (index === 8 && permissionToChange.rolPriPrivileges[index] === "0") {
        //    permissionToChange.rolPriPrivileges = "1".repeat(permissionToChange.rolPriPrivileges.length)
        //}
        else {
            permissionToChange.rolPriPrivileges = permissionToChange.rolPriPrivileges.substring(0, index) + '1' + permissionToChange.rolPriPrivileges.substring(index + 1);
        }
        let newPermissions = permissionsArr.map(p => p.rolPriPageId === page ? permissionToChange : p)
        updatePermissions();
        setPermissionsArr([...newPermissions])
        setPages([...pages]);
    }
    const updatePermissions = async () => {
        setIsTableLoading(true)
        let response = await PrivilegeManagerService.updatePrivs(permissionsArr)
        //if (response) {
        //    setSuccessDialog({
        //        isOpen: true,
        //        title: 'Privilege Updated',
        //        subTitle: <><Box component="div">Privileges for {selectedRole.rolName} have been updated</Box></>
        //    })
        //}
        setIsTableLoading(false)
        console.log(response)
    }

    const columns = [
        {
            field: 'pagName',
            title: 'Menu',
            render: rowData => rowData.pagUrl === 'link' ? <b>{rowData.pagName}</b> : rowData.pagName
        },
        {
            field: 'pagUrl',
            title: 'url or menu link',
            render: rowData => rowData.pagUrl !== 'link' ? <Link to={rowData.pagUrl} style={{ textDecoration: 'none', color: theme.palette.type === 'light' ? theme.palette.info.dark : '#86C1D4' }}>{rowData.pagUrl}</Link> : `${rowData.pagName} Link`
        },
        {
            field: 'privilege',
            title: 'Privilege',
            cellStyle: {
                padding: 0
            },
            render: rowData => {

                let toRender;
                for (let i = 0; i < selectedRolePrivs.length; i++) {
                    if (rowData.pagId === selectedRolePrivs[i].rolPriPageId) {
                        let s = selectedRolePrivs[i].rolPriPrivileges.split('')
                        if (s.length > 2) {
                            toRender = (
                                <>
                                    {s.map((x, i) => x === "1" ? <button id='enBtn' key={i} disabled={allowAction("503", "edit") ? false : true} className={classes.enableButton} onClick={() => handleClickPermission(rowData.pagId, i)} >{i}</button> : <button id='disBtn' key={i * 13} disabled={allowAction("503", "edit") ? false : true} className={classes.disableButton} onClick={() => handleClickPermission(rowData.pagId, i)}>{i}</button>)}
                                </>
                            )
                        } else {
                            toRender = (
                                <>
                                    <FormControlLabel
                                        className={classes.formControlLabel}
                                        control={
                                            <Switch color='primary' disabled={allowAction("503", "edit") ? false : true} checked={s[0] === '1'} onChange={() => handleClickPermission(rowData.pagId, i)} />
                                        }
                                        label={<Typography variant="body2" color="textSecondary">{s[0] === '1' ? 'Active' : 'Disabled'}</Typography>}
                                    />
                                </>
                                )
                        }
                    }
                }
                return toRender;
            }
        },
    ]

    if (isLoadingRoles || isLoadingPages) {
        return (
            <Backdrop className={classes.backdrop} open={isLoadingRoles || isLoadingPages}>
                <CircularProgress color="inherit" />
            </Backdrop>
        )
    }
    return (
        <>
            <Container>
                <div className={classes.pageTitleDiv}>
                    <h2 className={classes.pageTitle}>
                        Privilege Manager
                    </h2>
                </div>
                <Card className={classes.cardFiltersContainer}>
                    <CardContent className={classes.cardContentForFiltersContainer}>
                        <Grid container spacing={2} className={classes.filterDropdownsContainer} >
                            <Grid item xs={12} sm={12} md={6} lg={5} className={classes.gridItem}>
                                <Autocomplete
                                    disabled={false}
                                    id="role"
                                    name="role"
                                    //className={classes.textField}
                                    fullWidth
                                    disableClearable
                                    blurOnSelect={true}
                                    options={roleOptions}
                                    value={selectedRole}
                                    getOptionSelected={(option, value) => option.rolName === value.rolName}
                                    getOptionLabel={(option) => option.rolName}
                                    renderOption={option => <>{option.rolName}</>}
                                    onChange={(event, newValue) => {
                                        handleRoleChange(event, newValue);
                                    }}
                                    renderInput={(params) => <TextField {...params}
                                        id="role"
                                        name="role"
                                        size="small"
                                        variant="outlined"
                                        label='Role'
                                    />}
                                />
                            </Grid>
                            <Grid item xs={12} sm={12} md={6} lg={7} className={classes.gridItem}>
                                <div style={{display: 'flex', flexDirection: 'row', justifyContent: 'space-between', width: '100%'}}>
                                {/*<Button*/}
                                {/*        variant='contained'*/}
                                {/*        color='primary'*/}
                                {/*        style={{ width: 180 }}*/}
                                {/*        onClick={updatePermissions}*/}
                                {/*>*/}
                                {/*    Update */}
                                {/*</Button>*/}
                                {/*<Button*/}
                                {/*        variant='contained'*/}
                                {/*        color='primary'*/}
                                {/*        style={{ width: 180 }}*/}
                                {/*        size='small'*/}
                                {/*        //startIcon={<Cog style={{ color: 'gray' }} />}*/}
                                {/*        onClick={() => history.push('/admin/role-master/home')}*/}
                                {/*>*/}
                                {/*        Role Master*/}
                                {/*</Button>*/}
                                </div>
                            </Grid>
                            <Grid item xs={6} sm={12} md={12} lg={12} className={classes.gridItem} style={{ flexWrap: 'wrap' }}>
                                <Typography color='textSecondary' variant='caption'> 0: Access/View &nbsp;&nbsp;&nbsp;&nbsp;</Typography>
                                <Typography color='textSecondary' variant='caption'> 1: Update/Edit &nbsp;&nbsp;&nbsp;&nbsp;</Typography>
                                <Typography color='textSecondary' variant='caption'> 2: Create/Add &nbsp;&nbsp;&nbsp;&nbsp;</Typography>
                                <Typography color='textSecondary' variant='caption'> 3: Submit &nbsp;&nbsp;&nbsp;&nbsp;</Typography>
                                <Typography color='textSecondary' variant='caption'> 4: Cancel &nbsp;&nbsp;&nbsp;&nbsp;</Typography>
                                <Typography color='textSecondary' variant='caption'> 5: Delete &nbsp;&nbsp;&nbsp;&nbsp;</Typography>
                                <Typography color='textSecondary' variant='caption'> 6: Reject &nbsp;&nbsp;&nbsp;&nbsp;</Typography>
                                <Typography color='textSecondary' variant='caption'> 7: Approve(1) &nbsp;&nbsp;&nbsp;&nbsp;</Typography>
                                <Typography color='textSecondary' variant='caption'> 8: Approve(2) &nbsp;&nbsp;&nbsp;&nbsp;</Typography>
                                <Typography color='textSecondary' variant='caption'> 9: Approve(3) &nbsp;&nbsp;&nbsp;&nbsp;</Typography>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>

                <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>

                    <MaterialTable
                        style={{ width: '100%' }}
                        title=""
                        icons={TableIcons}
                        isLoading={isTableLoading || isLoadingPages}
                        components={{
                            Toolbar: props => (
                                <div style={{ width: 0, height: 0 }}>
                                    <MTableToolbar {...props} />
                                </div>
                            ),
                            Header: props => (
                                <>
                                    <MTableHeader {...props} />
                                </>
                            ),
                        }}
                        data={query =>
                            new Promise((resolve, reject) => {
                                // prepare your data and then call resolve like this:
                                resolve({
                                    data: pages,
                                    page: pageNumber,
                                    totalCount: rowParents.length
                                });
                            })
                        }
                        columns={columns}
                        parentChildData={(row, rows) => rows.find(a => {
                            return a.pagId === row.pagParentId
                        })
                        }

                        options={{
                            actionsCellStyle: { paddingLeft: 0, paddingRight: 10 },
                            sorting: true,
                            maxBodyHeight: 530,
                            showTitle: false,
                            toolbar: false,
                            headerStyle: headerStyle,
                            rowStyle: rowData => {
                                if (rowData.pagParentId === "0") {
                                    return linkRowStyle;
                                } else if (Number(rowData.pagParentId) < 10) {
                                    return middleRowStyle
                                } else {
                                    return childRowStyle;
                                }
                            },
                            initialPage: pageNumber,
                            pageSize: pageSize
                        }}
                        onChangePage={(page) => setPageNumber(page)}
                        onChangeRowsPerPage={(rows) => setPageSize(rows)}
                    />
                </Grid>

            </Container >
            <SuccessDialog
                successDialog={successDialog}
                setSuccessDialog={setSuccessDialog}
            />
            <Backdrop className={classes.backdrop} open={false}>
                <CircularProgress color="inherit" />
            </Backdrop>
        </>

    );
}